import { useI18n, useTranslate } from "@/lib/i18n";
import { receiptFooterForLocale } from "../lib/receiptFooter";
import { useMoney } from "@/shared/ui";
import { roundMoney } from "@/modules/pos/lib/pricing";
import { refundMethodCopy } from "@/modules/pos/lib/refundMethodCopy";
import {
  formatRefundStamp,
  type RefundDocument,
} from "@/modules/pos/lib/refundDocument";
import type { RestaurantSettings } from "@/lib/api/types";

export type {
  RefundDocument,
  RefundReceiptLine,
} from "@/modules/pos/lib/refundDocument";

/**
 * The refund receipt — trap 9's document.
 *
 * A refund receipt is a customer-facing TAX document, and it must be as correct
 * as the cheque it reverses. It is its own paper and not a reprint with a minus
 * sign: it names the bill it reverses, carries its own reference, lists the
 * plates that came back with the per-plate net / service / VAT split, states the
 * method the money left by, and carries the store's tax registration number —
 * all in Arabic first, with the English half of any name that has one
 * underneath.
 *
 * **Two provenances, one paper.** The ORIGINAL is minted at the till the moment
 * the command lands. A COPY is rebuilt from the invoice read's per-refund event
 * (`buildRefundReprint`) so the document survives a jammed printer, a reload,
 * another device and a customer who comes back tomorrow. A copy is a copy of ONE
 * REFUND — the same reference, the same plates, the same money, the same method,
 * the same reason and the same instant as the original — and it is still MARKED
 * as a copy, because a duplicate of a tax document that does not name itself a
 * duplicate is how one refund gets filed twice. See `refundDocument.ts`.
 *
 * **The instant is printed in the STORE's timezone**, never in UTC and never in
 * the tablet's: a 21:30 Cairo refund stamped in UTC prints 18:30, and a
 * late-night one prints yesterday's date on a paper that carries the tax
 * registration number.
 *
 * **What is NOT on this paper: the cashier's note.** The free-text reason note
 * is the internal record (R10) — the manager reads it in the shift review and
 * in the command log — and «الزبون عمل مشكلة» in the guest's hand is how the
 * notes turn into "ok" forever. The reason CODE's label is printed; the note is
 * not (business finding 10). It is not even on the invoice read, so this
 * component could not print it if it tried.
 *
 * **Why the total is the SERVER's and the lines are the till's.** The per-plate
 * split is computed by the client mirror of the allocation policy
 * (`refundAllocation.ts`), which reproduces the server's arithmetic digit for
 * digit and is pinned to the design's worked examples by its own spec. The
 * ledger is still the authority, so the paper's headline figure is the server's
 * own `refundTotal` delta. When the two agree — every case the mirror is tested
 * on — the document prints in full. When they DISAGREE, the lines are dropped
 * and the paper says so: a receipt is not the place to hand a customer a split
 * the books do not recognise, and a silent per-plate breakdown that fails to
 * sum to the amount refunded is exactly the defect this rule exists to prevent.
 *
 * Print plumbing: the same 80 mm `.receipt` scope the customer receipt, the
 * kitchen chit and the X share, under its own `data-print-scope="refund"` so it
 * can never print superimposed on any of them (see `styles.css`).
 */
export function RefundReceipt({
  document: doc,
  settings,
  tableName,
}: {
  document: RefundDocument;
  settings: RestaurantSettings | null;
  tableName: string | null;
}) {
  const { locale, direction } = useI18n();
  /* D82: the two-value locale the footer rule takes. `locale` is the app's
     language tag; anything that is not English prints the Arabic footer. */
  const footerLocale = locale === "en" ? "en" : "ar";
  const t = useTranslate();
  const format = useMoney();
  const { invoice, lines, serverTotal } = doc;

  // The mirror against the ledger. `roundMoney` on both sides so two 2dp
  // figures are compared as money and not as floats.
  const clientTotal = roundMoney(
    lines.reduce((sum, line) => roundMoney(sum + line.amount), 0),
  );
  const linesAgree = clientTotal === roundMoney(serverTotal);

  const net = roundMoney(
    lines.reduce((sum, line) => roundMoney(sum + line.netAmount), 0),
  );
  const service = roundMoney(
    lines.reduce((sum, line) => roundMoney(sum + line.serviceChargeAmount), 0),
  );
  const vat = roundMoney(
    lines.reduce((sum, line) => roundMoney(sum + line.vatAmount), 0),
  );

  return (
    <div
      className="receipt refund-receipt"
      dir={direction}
      lang={locale}
      aria-hidden="true"
    >
      {settings?.receiptHeader ? (
        <div style={{ textAlign: "center", fontWeight: 700 }}>
          {settings.receiptHeader}
        </div>
      ) : null}

      {/* The document names ITSELF, first and in bold. A customer holding two
          slips from one meal must be able to tell them apart at a glance, and
          a refund that looks like a receipt is a refund that gets filed as a
          second sale. */}
      <div style={{ textAlign: "center", fontWeight: 800, fontSize: "13px" }}>
        {t((m) => m.pos.refundReceiptTitle)}
      </div>

      {/* A COPY says so, in bold, at the top. A duplicate of a tax document
          that does not name itself a duplicate is how one refund is filed
          twice. */}
      {doc.isCopy ? (
        <div style={{ textAlign: "center", fontWeight: 800, fontSize: "11px" }}>
          {t((m) => m.pos.refundReceiptCopyTitle)}
        </div>
      ) : null}

      {/* The TRN, on this paper as on the cheque: a tax document that reverses
          a tax document must carry the same registration. */}
      {settings?.taxRegistrationNumber ? (
        <div style={{ textAlign: "center", fontSize: "9px" }}>
          {t((m) => m.invoices.taxRegistrationNumber)}:{" "}
          <bdi dir="ltr">{settings.taxRegistrationNumber}</bdi>
        </div>
      ) : null}

      {/* Which bill this reverses — the customer's own reference, and the one
          a manager reconciles against. */}
      <div style={{ textAlign: "center", marginTop: "1mm" }}>
        {t((m) => m.pos.refundReceiptAgainst, {
          sequence: String(invoice.dailySequence),
        })}
        {tableName ? ` · ${tableName}` : ""}
      </div>
      <div style={{ textAlign: "center", fontSize: "9px" }}>
        {t((m) => m.pos.refundReceiptBillDate, {
          date: invoice.businessDate,
        })}
      </div>
      {/* THE SLIP'S OWN REFERENCE (business finding 4). Two partial refunds
          off one bill are otherwise two pieces of paper that differ only by a
          timestamp, and an accountant reconciling a month cannot cite either.
          `dir="ltr"` — it is an alphanumeric handle, not prose. */}
      {doc.reference ? (
        <div style={{ textAlign: "center", fontSize: "9px" }}>
          {t((m) => m.pos.refundReceiptReference)}:{" "}
          <bdi dir="ltr">{doc.reference}</bdi>
        </div>
      ) : null}

      {/* WHEN THE MONEY WENT BACK, in the STORE's timezone (business
          DEFECT-2), and now on a COPY too — the refund's `occurred_at` is on
          the invoice read.

          LABELLED, deliberately. It used to print as a bare timestamp directly
          under «تاريخ الفاتورة», where a guest cannot tell which of the two
          dates is which; and on a copy it now sits beside the copy's own print
          time, where an unlabelled pair would be worse still. Not `dir="ltr"`
          on the row: an Arabic label with Western figures after it is a mixed
          run, and forcing LTR reorders the Arabic — the stamp itself is wrapped
          instead. */}
      {doc.refundedAt ? (
        <div style={{ textAlign: "center", fontSize: "9px" }}>
          {t((m) => m.pos.refundReceiptMovedAt, {
            stamp: formatRefundStamp(
              doc.refundedAt,
              settings?.timezone,
              locale,
            ),
          })}
        </div>
      ) : null}

      <hr style={{ margin: "2mm 0", borderTop: "1px solid #000" }} />

      {linesAgree ? (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <tbody>
            {lines.map((line) => (
              <tr key={line.itemId}>
                <td style={{ verticalAlign: "top" }}>
                  {line.nameAr}
                  {line.nameEn ? (
                    <div dir="ltr" style={{ fontSize: "10px" }}>
                      {line.nameEn}
                    </div>
                  ) : null}
                  {/* The SWAP (D61), both halves, under the same rule the name
                      above follows: Arabic first, the other language under it,
                      never instead of it. A guest refunded for a substituted
                      drink was being handed a tax document naming a drink they
                      were never served — while the settle receipt for the very
                      same plate named the swap. Snapshotted onto the line, so a
                      copy printed next week says it too. */}
                  {line.swapAr ? (
                    <div style={{ fontSize: "10px" }} data-swap={line.itemId}>
                      {line.swapAr}
                    </div>
                  ) : null}
                  {line.swapEn && line.swapEn !== line.swapAr ? (
                    <div dir="ltr" style={{ fontSize: "10px" }}>
                      {line.swapEn}
                    </div>
                  ) : null}
                  {/* R3, per plate, on the paper. Two plates that cost the
                      same can carry VAT shares a piastre apart — the customer
                      reading this slip is entitled to see which is which
                      without the till being consulted. */}
                  {/* NOT `dir="ltr"`: Arabic labels with Western figures
                      between them — `useMoney` already pins the digits, and
                      forcing LTR on a mixed run reorders the Arabic. The
                      SIGNED figures keep it: a leading minus is weak. */}
                  <div style={{ fontSize: "9px" }}>
                    {[
                      `${t((m) => m.invoices.netBeforeCharges)} ${format(line.netAmount)}`,
                      line.serviceChargeAmount > 0
                        ? `${t((m) => m.invoices.serviceCharge)} ${format(line.serviceChargeAmount)}`
                        : null,
                      line.vatAmount > 0
                        ? `${t((m) => m.invoices.vat)} ${format(line.vatAmount)}`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                </td>
                {/* Signed and `dir="ltr"`: a leading minus is weak-directional
                    and flips to the far side of the digits in an RTL run.

                    `whiteSpace: "nowrap"` — an AMOUNT AND ITS CURRENCY ARE ONE
                    TOKEN. Without it, 80mm paper broke «- 172.37 جنيه» after
                    the digits and dropped «جنيه» onto the next line, where in
                    Arabic it landed against the plate's English name and
                    printed «جنيهChicken pasta». This is a customer-facing tax
                    document (trap 9, D82) and the figure on it is the money
                    handed back. The plate's own column wraps instead, which it
                    is already built to do. */}
                <td
                  dir="ltr"
                  style={{
                    textAlign: "end",
                    verticalAlign: "top",
                    whiteSpace: "nowrap",
                  }}
                >
                  {`- ${format(line.amount)}`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div style={{ fontSize: "10px" }}>
          {t((m) => m.pos.refundReceiptSplitUnavailable)}
        </div>
      )}

      <hr style={{ margin: "2mm 0" }} />

      {linesAgree ? (
        <>
          <PaperRow
            label={t((m) => m.invoices.netBeforeCharges)}
            value={`- ${format(net)}`}
          />
          {service > 0 ? (
            <PaperRow
              label={t((m) =>
                invoice.pricesIncludeTax
                  ? m.invoices.serviceChargeIncluded
                  : m.invoices.serviceCharge,
              )}
              value={`- ${format(service)}`}
            />
          ) : null}
          {vat > 0 ? (
            <PaperRow
              label={t((m) =>
                invoice.pricesIncludeTax
                  ? m.invoices.vatIncluded
                  : m.invoices.vat,
              )}
              value={`- ${format(vat)}`}
            />
          ) : null}
        </>
      ) : null}

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontWeight: 700,
          fontSize: "13px",
          marginTop: "1mm",
        }}
      >
        <span>{t((m) => m.pos.refundBackToCustomer)}</span>
        <span dir="ltr">{`- ${format(serverTotal)}`}</span>
      </div>

      {doc.method !== null ? (
        <PaperRow
          label={t((m) => m.pos.refundMethodLabel)}
          // The SAME mapping the sheet reads (`refundMethodCopy`), so the paper
          // in the customer's hand and the screen the cashier confirmed on
          // cannot name the method differently.
          value={t(refundMethodCopy(doc.method).label)}
        />
      ) : null}
      {/* The reason CODE's label, and only it. The cashier's free-text note is
          the internal record (R10, business finding 10) and never goes into the
          guest's hand — it is on the shift review and in the command log. */}
      {doc.reasonLabel ? (
        <PaperRow
          label={t((m) => m.pos.refundReason)}
          value={doc.reasonLabel}
        />
      ) : null}

      {/* What a COPY is, said on the copy: it covers every refund on this bill
          to date, and the reason, the time and the reference of each one are on
          the original slip and in the shift review. Stated rather than left to
          be inferred from three missing lines. */}
      {doc.isCopy && doc.reference ? (
        <div style={{ marginTop: "2mm", fontSize: "9px" }}>
          {t((m) => m.pos.refundReceiptCopyNote, {
            reference: doc.reference,
          })}
        </div>
      ) : null}

      {/* The copy's OWN date and time (MINOR 11), BESIDE the refund's instant
          rather than instead of it: the stamp above is when the MONEY MOVED
          (the ledger's `occurred_at`, now on the wire), and this one is when
          this piece of paper came out of the printer. Both labelled, in the
          store's own zone, because a tax document with one unlabelled date on
          it is a document a reader has to guess about. */}
      {doc.isCopy ? (
        <div style={{ marginTop: "1mm", fontSize: "9px" }}>
          {t((m) => m.pos.refundReceiptCopyPrintedAt, {
            stamp: formatRefundStamp(
              new Date().toISOString(),
              settings?.timezone,
              locale,
            ),
          })}
        </div>
      ) : null}

      {/* R5, on the paper: the original cheque is not rewritten, and a customer
          comparing the two slips must not read the difference as an error. */}
      <div style={{ marginTop: "2mm", fontSize: "9px" }}>
        {t((m) => m.pos.refundReceiptOriginalStands)}
      </div>

      {/* D82 — THIS slip's own language. One stored footer used to print under
          both, so an Arabic line ended an English receipt. The choice, and the
          fall back to the Arabic when a store has written no English one, is
          `receiptFooterForLocale` — one tested rule, because there are two
          customer-facing slips and a rule copied into both diverges. */}
      {receiptFooterForLocale(settings, footerLocale) ? (
        <div
          style={{ textAlign: "center", marginTop: "3mm" }}
          data-testid="receipt-footer"
        >
          {receiptFooterForLocale(settings, footerLocale)}
        </div>
      ) : null}
    </div>
  );
}

/** One label/value line on the paper. */
function PaperRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between" }}>
      <span>{label}</span>
      <span dir="ltr">{value}</span>
    </div>
  );
}
